import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { t } from "i18next";

import { useAuth } from "@/src/auth/hooks/useAuth";

import { ConsumerQueryKeys } from "../queries/consumer/consumerFunctions";
import {
  DealerQueryKeys,
  fetchAuctionItem
} from "../queries/dealer/dealerFunctions";
import { NotificationQueryKeys } from "../queries/notifications/notificationFunctions";
import { announcementsAtom } from "../state/announcementAtom";
/**
 * Badge notification system:
 *
 * Badge notifications are managed through two systems:
 * 1. IoT events with BADGE_NOTIFICATION type that invalidate badge counts
 * 2. A counter-based badgesAtom that tracks pending/inNegotiation/accepted/declined counts
 *
 * The badge counts are fetched from the API endpoints (/badges)
 * Rather than directly incrementing counters in IoT handlers, we invalidate the badges query
 * to fetch fresh counts from the server.
 */
import { badgesAtom } from "../state/atoms/badges";
import { notificationAtom } from "../state/notificationAtom";
import {
  Announcement,
  AuctionState,
  BidState,
  DealerAuctionDTO,
  IoTEventType,
  IoTMessage,
  IoTPayload
} from "../types/types";
import { getAnnouncements } from "./announcementUtils";
import { resolveMakeModelRegistrationNumber } from "./auctionItemUtils";
import { storePatchNotification } from "./patchUtils";

import { CONNECTION_STATE_CHANGE, ConnectionState } from "@aws-amplify/pubsub";
import { Hub } from "aws-amplify/utils";
import { useSetAtom } from "jotai";

type NotificationIoTMessage = {
  [key in IoTEventType]?: boolean;
};

/**
 * Object to check if IoTMessage is related to a `UserNotification`.
 * If key is true it should trigger fetching new notifications for user
 */
const notificationIoTMessages: NotificationIoTMessage = {
  [IoTEventType.BID_DECLINED]: true,
  [IoTEventType.ADMIN_BID_DECLINED]: true,
  [IoTEventType.ADMIN_BID_REMOVED]: true,
  [IoTEventType.ADMIN_BID_MODIFIED]: true,
  [IoTEventType.BID_UPDATED]: true,
  [IoTEventType.BID_UPDATED_AND_STATE_CHANGE]: true,
  [IoTEventType.NEW_ANSWER]: true,
  [IoTEventType.NEW_BIDS_OUTBIDDED]: true,
  [IoTEventType.NEW_QUESTION]: true,
  [IoTEventType.QA_DELETED]: true,
  [IoTEventType.NEW_NEGOTIATION_MESSAGE]: true,
  [IoTEventType.NEW_PATCH]: true,
  [IoTEventType.AUCTION_MARKED_SOLD]: true,
  [IoTEventType.AUCTION_ADDITIONAL_INFO_ADDED]: true,
  [IoTEventType.BADGE_NOTIFICATION_PENDING]: true,
  [IoTEventType.BADGE_NOTIFICATION_IN_NEGOTIATION]: true,
  [IoTEventType.BADGE_NOTIFICATION_ACCEPTED]: true,
  [IoTEventType.BADGE_NOTIFICATION_DECLINED]: true
};

const useIOT = () => {
  const queryClient = useQueryClient();
  const setNotification = useSetAtom(notificationAtom);
  const setAnnouncements = useSetAtom(announcementsAtom);
  const setBadges = useSetAtom(badgesAtom);
  const auth = useAuth();

  // Helper function to update badge counts consistently across different event types
  const updateBadgeCounts = (updateType: string) => {
    queryClient.invalidateQueries({
      queryKey: ["badges"]
    });

    // Apply badge count updates based on the type of update
    switch (updateType) {
      case "ACCEPTED":
        setBadges((currentBadges) => ({
          ...currentBadges,
          inNegotiation: Math.max(0, currentBadges.inNegotiation - 1),
          pending: Math.max(0, currentBadges.pending - 1),
          declined: Math.max(0, currentBadges.declined - 1),
          accepted: currentBadges.accepted + 1
        }));
        break;
      case "DECLINED":
        setBadges((currentBadges) => ({
          ...currentBadges,
          inNegotiation: Math.max(0, currentBadges.inNegotiation - 1),
          pending: Math.max(0, currentBadges.pending - 1),
          accepted: Math.max(0, currentBadges.accepted - 1),
          declined: currentBadges.declined + 1
        }));
        break;
      case "PENDING":
        setBadges((currentBadges) => ({
          ...currentBadges,
          inNegotiation: Math.max(0, currentBadges.inNegotiation - 1),
          accepted: Math.max(0, currentBadges.accepted - 1),
          declined: Math.max(0, currentBadges.declined - 1),
          pending: currentBadges.pending + 1
        }));
        break;
      case "IN_NEGOTIATION":
        setBadges((currentBadges) => ({
          ...currentBadges,
          pending: Math.max(0, currentBadges.pending - 1),
          accepted: Math.max(0, currentBadges.accepted - 1),
          declined: Math.max(0, currentBadges.declined - 1),
          inNegotiation: currentBadges.inNegotiation + 1
        }));
        break;
      // Add more cases here if needed for other badge types
    }
  };

  const handleSocketMessage = (payload: IoTPayload) => {
    // Invalidate Notifications if IoT event type is related to notifications
    const isAuctionEndedEvent =
      payload.message.type === IoTEventType.AUCTION_STATE_CHANGE &&
      payload.message.auctionState === AuctionState.ENDED;

    const isBadgeNotification =
      payload.message.type === IoTEventType.BADGE_NOTIFICATION_PENDING ||
      payload.message.type === IoTEventType.BADGE_NOTIFICATION_IN_NEGOTIATION ||
      payload.message.type === IoTEventType.BADGE_NOTIFICATION_ACCEPTED ||
      payload.message.type === IoTEventType.BADGE_NOTIFICATION_DECLINED;

    if (notificationIoTMessages[payload.message.type] || isAuctionEndedEvent) {
      queryClient.invalidateQueries({
        queryKey: [NotificationQueryKeys.NOTIFICATIONS]
      });

      // Also invalidate badge counts when notifications change
      queryClient.invalidateQueries({
        queryKey: ["badges"]
      });
    }

    // Handle specific badge notifications
    if (isBadgeNotification) {
      // Process based on badge notification type
      if (payload.message.type === IoTEventType.BADGE_NOTIFICATION_ACCEPTED) {
        updateBadgeCounts("ACCEPTED");
      } else if (
        payload.message.type === IoTEventType.BADGE_NOTIFICATION_DECLINED
      ) {
        updateBadgeCounts("DECLINED");
      }
      return;
    }

    // Handle patch notifications first
    if (payload.message.type === IoTEventType.NEW_PATCH) {
      processDealerMessage(payload.message);
      return;
    }

    if (payload.topic === "announcements") {
      handleNewAnnouncement(payload);
    } else if (auth.isDealer) {
      handleDealerSocketMessage(payload);
    } else if (auth.isConsumer) {
      handleConsumerSocketMessage(payload);
    }
  };

  /**
   * Locates the auction with matchin id from the array,
   * and replaces the contents with the provided item data.
   *
   * @param currentArray
   * @param newItem
   * @returns updatedArray
   */

  const updateDealerAuctionArray = (
    currentArray: DealerAuctionDTO[],
    newItem: DealerAuctionDTO
  ): DealerAuctionDTO[] => {
    const updatedArray = [...currentArray];
    updatedArray.forEach((item, i) => {
      if (item.id === newItem.id) {
        updatedArray[i] = { ...newItem };
      }
    });
    return updatedArray;
  };

  const updateDealerAuctionArrayWithPaging = (
    currentArray: DealerAuctionDTO[][],
    newItem: DealerAuctionDTO
  ): DealerAuctionDTO[][] => {
    const updatedArray = [...currentArray];
    updatedArray.forEach((page) =>
      page.forEach((item, i) => {
        if (item.id === newItem.id) {
          page[i] = { ...newItem };
        }
      })
    );
    return updatedArray;
  };

  /**
   * Updates the Dealer React Query states.
   *
   * @param auctionItem
   */
  const updateDealerQueryStates = (auction: DealerAuctionDTO) => {
    // AUCTION
    const currentAuctionState: DealerAuctionDTO | null | undefined =
      queryClient.getQueryData([DealerQueryKeys.AUCTION, auction.id]);
    if (currentAuctionState?.id === auction.id) {
      queryClient.setQueryData([DealerQueryKeys.AUCTION, auction.id], auction);
    }

    // ENDED_AUCTIONS
    const currentEndedAuctionState: DealerAuctionDTO[][] | null | undefined =
      queryClient.getQueryData([DealerQueryKeys.ENDED_AUCTIONS]);
    if (currentEndedAuctionState && currentEndedAuctionState.length > 0) {
      const updatedData = updateDealerAuctionArrayWithPaging(
        currentEndedAuctionState,
        auction
      );
      queryClient.setQueryData([DealerQueryKeys.ENDED_AUCTIONS], updatedData);
    }

    // AUCTIONS
    const currentAuctionsState: DealerAuctionDTO[] | null | undefined =
      queryClient.getQueryData([DealerQueryKeys.AUCTIONS]);
    if (currentAuctionsState && currentAuctionsState.length > 0) {
      const updatedData = updateDealerAuctionArray(
        currentAuctionsState,
        auction
      );
      queryClient.setQueryData([DealerQueryKeys.AUCTIONS], updatedData);
    }

    // MYAUCTIONS
    const currentMyAuctionsState: DealerAuctionDTO[] | null | undefined =
      queryClient.getQueryData([DealerQueryKeys.MY_ACTIVE_AUCTIONS]);
    if (currentMyAuctionsState && currentMyAuctionsState.length > 0) {
      const updatedData = updateDealerAuctionArray(
        currentMyAuctionsState,
        auction
      );
      queryClient.setQueryData(
        [DealerQueryKeys.MY_ACTIVE_AUCTIONS],
        updatedData
      );
    }
  };

  const processDealerMessage = (
    message: IoTMessage,
    auctionItem?: DealerAuctionDTO
  ) => {
    const targetVehicleInfo = auctionItem
      ? resolveMakeModelRegistrationNumber(auctionItem)
      : "";

    let reactQueryStateUpdateNeeded = false;
    const reactQueryStateInvalidationNeeded = true; // all messages (so far) require state update

    switch (message.type) {
      case IoTEventType.AUCTION_STATE_CHANGE:
        if (
          message.auctionState === AuctionState.ENDED &&
          auctionItem?.myHighestBid
        ) {
          setNotification({
            text: t("notifications.auctionEnded", {
              targetVehicleInfo: targetVehicleInfo
            }),
            variant: "info",
            auctionId: auctionItem ? auctionItem.id : undefined
          });

          // Invalidate badge counts when an auction ends
          queryClient.invalidateQueries({
            queryKey: ["badges"]
          });
        } else if (message.auctionState === AuctionState.CANCELLED) {
          setNotification({
            text: t("notifications.auctionCancelled", {
              targetVehicleInfo: targetVehicleInfo
            }),
            variant: "error",
            auctionId: auctionItem ? auctionItem.id : undefined
          });
        } else if (
          message.auctionState === AuctionState.OFFER_ACCEPTED &&
          auctionItem?.myHighestBid?.state === BidState.ACCEPTED
        ) {
          // Mark as accepted
          updateBadgeCounts("ACCEPTED");

          setNotification({
            text: t("notifications.auctionAccepted", {
              targetVehicleInfo: targetVehicleInfo
            }),
            variant: "success",
            auctionId: auctionItem ? auctionItem.id : undefined
          });
        } else if (message.auctionState === AuctionState.REJECTED_BY_DEALER) {
          // Mark as declined
          updateBadgeCounts("DECLINED");
        } else {
          // Do nothing
        }
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.BID_FAILED:
        setNotification({
          text: t("notifications.biddingFailure"),
          variant: "error"
        });
        // No need to update query state as there are no changes
        break;

      case IoTEventType.BID_FAILED_OTHER_BID_MACHINE_GOT_BID:
        setNotification({
          text: t("notifications.bidFailedOtherBidMachineGotBid"),
          variant: "error"
        });
        // No need to update query state, NEW_BIDS message will follow
        break;

      case IoTEventType.BID_SUCCESS:
        setNotification({
          text: t("notifications.bidSuccess"),
          variant: "success"
        });
        // No need to update query state, NEW_BIDS message will follow
        break;

      case IoTEventType.BID_SUCCESS_OTHER_BID_MACHINE_RAISED:
        setNotification({
          text: t("notifications.bidSuccessOtherBidMachineRaised"),
          variant: "error"
        });
        // No need to update query state, NEW_BIDS message will follow
        break;

      case IoTEventType.NEW_BIDS:
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.NEW_BIDS_OUTBIDDED:
        setNotification({
          text: t("notifications.newBidsOutBidded", {
            targetVehicleInfo: targetVehicleInfo
          }),
          variant: "info",
          auctionId: auctionItem ? auctionItem.id : undefined
        });
        // No need to update query state, NEW_BIDS message will follow
        break;

      case IoTEventType.BID_UPDATED:
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.BID_DECLINED:
        setNotification({
          text: t("notifications.bidDeclined", {
            targetVehicleInfo: targetVehicleInfo
          }),
          variant: "error",
          auctionId: auctionItem ? auctionItem.id : undefined
        });
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.ADMIN_BID_DECLINED:
        // No toast: user sees the in-app notification only (different copy than seller decline)
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.ADMIN_BID_REMOVED:
        setNotification({
          text: t("notifications.adminBidRemoved", {
            targetVehicleInfo: targetVehicleInfo
          }),
          variant: "error",
          auctionId: auctionItem ? auctionItem.id : undefined
        });
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.ADMIN_BID_MODIFIED:
        setNotification({
          text: t("notifications.adminBidModified", {
            targetVehicleInfo: targetVehicleInfo
          }),
          variant: "info",
          auctionId: auctionItem ? auctionItem.id : undefined
        });
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.NEW_NEGOTIATION_MESSAGE:
        setNotification({
          text: t("notifications.newNegotiationMessage", {
            targetVehicleInfo: targetVehicleInfo
          }),
          variant: "info",
          auctionId: auctionItem ? auctionItem.id : undefined
        });
        // Update badge counts to transition from pending to negotiation
        updateBadgeCounts("IN_NEGOTIATION");
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.NEW_AUCTION:
        break;

      case IoTEventType.NEW_ANSWER:
        setNotification({
          text: t("notifications.newAnswerToQuestion", {
            targetVehicleInfo: targetVehicleInfo
          }),
          variant: "info",
          auctionId: auctionItem ? auctionItem.id : undefined
        });
        reactQueryStateUpdateNeeded = true;
        break;

      case IoTEventType.NEW_PATCH:
        if (auth.isDealer && auth.isAuthenticated) {
          storePatchNotification();
          // No need to modify badges for patch notifications
          setNotification({
            text: t("notifications.newInstructionsAvailable"),
            variant: "info"
          });
        }
        break;

      default:
        console.warn("Unknown IoT message received. Skipped.");
    }

    // Update / invalidate & refresh the react query state as necessary
    if (reactQueryStateUpdateNeeded) {
      if (auctionItem) {
        updateDealerQueryStates(auctionItem);
        queryClient.invalidateQueries({
          queryKey: [DealerQueryKeys.ENDED_AUCTIONS]
        });
      }
    } else if (reactQueryStateInvalidationNeeded) {
      queryClient.invalidateQueries({ queryKey: [DealerQueryKeys.AUCTION] });
      queryClient.invalidateQueries({ queryKey: [DealerQueryKeys.AUCTIONS] });
      queryClient.invalidateQueries({
        queryKey: [DealerQueryKeys.ENDED_AUCTIONS]
      });
      queryClient.invalidateQueries({
        queryKey: [DealerQueryKeys.AUCTIONS_SUMMARY]
      });
    } else {
      // Do nothing. Update/refresh not needed.
    }
  };

  /**
   * Dealer socket message handler
   */
  const handleDealerSocketMessage = (payload: IoTPayload) => {
    console.log("[Baana IOT] Dealer message handler", payload);
    const message = payload.message;

    if (message && message.type) {
      if (
        message.type === IoTEventType.BID_FAILED ||
        message.type === IoTEventType.BID_FAILED_OTHER_BID_MACHINE_GOT_BID ||
        message.type === IoTEventType.BID_SUCCESS ||
        message.type === IoTEventType.BID_SUCCESS_OTHER_BID_MACHINE_RAISED ||
        message.type === IoTEventType.NEW_AUCTION ||
        message.type === IoTEventType.NEW_PATCH
      ) {
        processDealerMessage(message);
      } else {
        if (message.auctionId !== undefined) {
          fetchAuctionItem(message.auctionId, (auctionItem) => {
            processDealerMessage(message, auctionItem);
          });
        } else {
          console.error(
            "Invalid IoT message received! Message type requires an auction id, but it was not provided."
          );
        }
      }
    } else {
      console.warn("Invalid IoT message received. Skipped.");
    }
  };

  /**
   * Consumer socket message handler
   */
  const handleConsumerSocketMessage = (payload: IoTPayload) => {
    console.log("[Baana IOT] Consumer message handler", payload);

    if (payload.topic !== `users/${auth.user?.id}`) {
      return;
    }

    const message = payload.message;

    switch (message.type) {
      case IoTEventType.BADGE_NOTIFICATION_ACCEPTED:
        // When we get a specific badge acceptance notification, update the badge counts
        updateBadgeCounts("ACCEPTED");
        break;
      case IoTEventType.BADGE_NOTIFICATION_DECLINED:
        // When we get a specific badge declined notification, update the badge counts
        updateBadgeCounts("DECLINED");
        break;
      case IoTEventType.NEW_BIDS:
        if (message.auctionId) {
          // No need to modify badges for new bids
          setNotification({
            text: t("socketMessages.newOfferReceived"),
            variant: "info"
          });
          // check for new notifications: FIRST_BID or TARGET_PRICE_REACHED
          queryClient.invalidateQueries({
            queryKey: [NotificationQueryKeys.NOTIFICATIONS]
          });
        }
        break;
      case IoTEventType.AUCTION_STATE_CHANGE:
        if (message.auctionId) {
          // Only invalidate badge counts when auction state changes
          // Badge state updates are handled by badge notifications
          queryClient.invalidateQueries({
            queryKey: ["badges"]
          });

          if (message.auctionState === AuctionState.ENDED) {
            setNotification({
              text: t("socketMessages.auctionHasEnded"),
              variant: "info"
            });
          }
          if (message.auctionState === AuctionState.OFFER_ACCEPTED) {
            setNotification({
              text: t("socketMessages.dealerOfferAccepted"),
              variant: "success"
            });
          }
          if (message.auctionState === AuctionState.REJECTED_BY_DEALER) {
            // Update badge counts when an auction is rejected by dealer (declined)
            updateBadgeCounts("DECLINED");

            setNotification({
              text: t("socketMessages.rejectedByDealer"),
              variant: "error"
            });
          }
        }
        break;
      case IoTEventType.NEW_NEGOTIATION_MESSAGE:
        updateBadgeCounts("IN_NEGOTIATION");
        queryClient.invalidateQueries({
          queryKey: [ConsumerQueryKeys.AUCTIONS]
        });
        // Invalidate negotiation messages query
        queryClient.invalidateQueries({
          queryKey: [ConsumerQueryKeys.AUCTION_NEGOTIATION]
        });
        // Invalidate specific auction if auctionId is available
        if (message.auctionId) {
          queryClient.invalidateQueries({
            queryKey: [`${ConsumerQueryKeys.AUCTIONS}/${message.auctionId}`]
          });
        }
        setNotification({
          text: t("socketMessages.newMessageReceived"),
          variant: "info"
        });
        break;
      case IoTEventType.NEW_QUESTION:
        setNotification({
          text: t("notifications.newQuestion"),
          variant: "info"
        });
        queryClient.invalidateQueries({
          queryKey: [ConsumerQueryKeys.AUCTIONS]
        });
        queryClient.invalidateQueries({
          queryKey: [`${ConsumerQueryKeys.AUCTIONS}/${message.auctionId}`]
        });
        break;
      case IoTEventType.BID_UPDATED_AND_STATE_CHANGE:
      case IoTEventType.AUCTION_MARKED_SOLD:
        setNotification({
          text: t("socketMessages.dealCompleted"),
          variant: "info"
        });
        // Use refetchQueries to force immediate data refresh
        queryClient.refetchQueries({
          queryKey: [ConsumerQueryKeys.AUCTIONS]
        });
        if (message.auctionId) {
          queryClient.refetchQueries({
            queryKey: [`${ConsumerQueryKeys.AUCTIONS}/${message.auctionId}`]
          });
        }
        break;
      case IoTEventType.QA_DELETED:
        queryClient.invalidateQueries({
          queryKey: [ConsumerQueryKeys.AUCTIONS]
        });
        queryClient.invalidateQueries({
          queryKey: [`${ConsumerQueryKeys.AUCTIONS}/${message.auctionId}`]
        });
        break;
      case IoTEventType.FINAL_OFFER_RECEIVED:
        // Final offer received - invalidate notifications and refetch auctions
        queryClient.invalidateQueries({
          queryKey: [NotificationQueryKeys.NOTIFICATIONS]
        });
        setNotification({
          text: t("components.notifications.FINAL_OFFER_RECEIVED.title"),
          variant: "info"
        });
        break;
      default:
      // Unknown message type. Ignore it.
    }

    if (
      message.type === IoTEventType.AUCTION_STATE_CHANGE ||
      message.type === IoTEventType.AUCTION_MARKED_SOLD ||
      message.type === IoTEventType.BID_UPDATED_AND_STATE_CHANGE ||
      message.type === IoTEventType.NEW_BIDS ||
      message.type === IoTEventType.BID_UPDATED ||
      message.type === IoTEventType.QA_DELETED ||
      message.type === IoTEventType.NEW_NEGOTIATION_MESSAGE ||
      message.type === IoTEventType.FINAL_OFFER_RECEIVED
    ) {
      queryClient.invalidateQueries({
        queryKey: [ConsumerQueryKeys.AUCTIONS]
      });
    }

    // Always invalidate specific auction data for targeted updates
    if (message.auctionId) {
      queryClient.invalidateQueries({
        queryKey: [ConsumerQueryKeys.AUCTIONS + "/" + message.auctionId]
      });
    }

    // Set message in atom state, used by specific components
    // currently: /consumer/ActiveAuction -view
    // setSocketMessage({
    //   message: message,
    //   topic: payload.topic
    // });
  };

  const handleNewAnnouncement = async (payload: IoTPayload) => {
    console.log("[Baana IOT] announcements handler", payload);
    const message = payload.message;

    if (message.type === IoTEventType.NEW_ANNOUNCEMENT) {
      try {
        const res: Announcement[] = await getAnnouncements();
        setAnnouncements(res);
      } catch (error) {
        setAnnouncements([]);
      }
    }
  };

  return { handleSocketMessage };
};

// Lazy singleton for PubSub instance
let pubsubInstance: InstanceType<
  typeof import("@aws-amplify/pubsub").PubSub
> | null = null;

const createPubSub = async () => {
  const { PubSub } = await import("@aws-amplify/pubsub");
  return new PubSub({
    region: import.meta.env.VITE_AWS_REGION as string,
    endpoint: import.meta.env.VITE_AWS_PUBSUB_ENDPOINT as string
  });
};

const getPubSub = async () => {
  if (!pubsubInstance) {
    pubsubInstance = await createPubSub();
  }
  return pubsubInstance;
};

// Active subscription handle + the topics/handler used to create it, so the
// connection can be torn down and re-established after device sleep/wake.
let activeSubscription: { unsubscribe: () => void } | null = null;
let activeTopics: string[] = [];
let activeOnMessage: ((data: IoTPayload) => void) | null = null;

// Latest Amplify PubSub connection state, captured via the Hub listener below.
let latestConnectionState: ConnectionState | null = null;
let connectionStateListenerRegistered = false;

// De-duplicates concurrent reconnect attempts (e.g. visibility + online + wake
// timer firing together) so we never open multiple connections at once.
let reconnectInFlight: Promise<boolean> | null = null;

// How long to wait for a freshly created MQTT connection to reach the Connected
// state after a reconnect before giving up and refetching anyway (best effort).
const CONNECTION_ESTABLISH_TIMEOUT_MS = 15_000;

// Resolvers waiting for the connection to reach the Connected state. They are
// notified by the Hub connection-state listener on every state change, and each
// removes itself once it settles (on Connected) or times out.
let connectionWaiters: Array<(state: ConnectionState) => boolean> = [];

/**
 * Register a one-time Hub listener that tracks the Amplify PubSub connection
 * state. This lets us decide whether a reconnect is actually needed after the
 * device wakes from sleep or the network is restored.
 */
const registerConnectionStateListener = () => {
  if (connectionStateListenerRegistered) {
    return;
  }

  Hub.listen("pubsub", (data) => {
    const { payload } = data;
    if (payload.event === CONNECTION_STATE_CHANGE) {
      const state = (payload.data as { connectionState: ConnectionState })
        .connectionState;
      latestConnectionState = state;

      // Notify waiters and drop the ones that have now settled.
      connectionWaiters = connectionWaiters.filter((notify) => !notify(state));
    }
  });

  connectionStateListenerRegistered = true;
};

/**
 * Resolve once the PubSub connection reaches the Connected state, or `false` if
 * it does not within `timeoutMs`. Used to defer the post-reconnect refetch until
 * the subscription is actually live, so no messages are lost during the MQTT
 * handshake.
 */
const waitForConnected = (timeoutMs: number): Promise<boolean> =>
  new Promise((resolve) => {
    if (latestConnectionState === ConnectionState.Connected) {
      resolve(true);
      return;
    }

    let settled = false;
    let timer: number;

    const waiter = (state: ConnectionState): boolean => {
      if (settled) {
        return true;
      }
      if (state === ConnectionState.Connected) {
        settled = true;
        window.clearTimeout(timer);
        resolve(true);
        return true;
      }
      return false;
    };

    timer = window.setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      connectionWaiters = connectionWaiters.filter((w) => w !== waiter);
      resolve(false);
    }, timeoutMs);

    connectionWaiters.push(waiter);
  });

/**
 * Create the PubSub subscription using the currently stored topics/handler and
 * keep a reference to the subscription handle so it can be unsubscribed later.
 */
const createSubscription = async () => {
  if (!activeTopics.length || !activeOnMessage) {
    return;
  }

  const onMessageReceived = activeOnMessage;
  const pubsub = await getPubSub();

  activeSubscription = pubsub.subscribe({ topics: activeTopics }).subscribe({
    next: (data) => onMessageReceived(data as unknown as IoTPayload),
    error: (error: Error) => console.error(error),
    complete: () => console.log("Done")
  });
};

/**
 * Tear down the current subscription and forget the stored topics/handler.
 * Call this on logout so a subsequent login (possibly as a different user)
 * re-subscribes to the correct user-specific topics instead of reusing the
 * previous session's subscription.
 */
const unsubscribeFromTopics = () => {
  activeSubscription?.unsubscribe();
  activeSubscription = null;
  activeTopics = [];
  activeOnMessage = null;
};

/**
 * Subscribe to IoT topics. If an existing subscription exists, unsubscribe
 * first to avoid duplicate message handling.
 * @param topics array of topics
 * @param onMessageReceived message handler
 */
const subscribeToTopics = async (
  topics: string[],
  onMessageReceived: (data: IoTPayload) => void
) => {
  registerConnectionStateListener();

  activeTopics = topics;
  activeOnMessage = onMessageReceived;

  activeSubscription?.unsubscribe();
  activeSubscription = null;

  await createSubscription();
};

/**
 * Tear down the current subscription and the PubSub instance, then re-subscribe.
 *
 * Dropping `pubsubInstance` is essential: Amplify caches the underlying MQTT
 * client per clientId, so simply re-subscribing would re-attach to the same
 * (dead) client. Recreating the instance forces a brand-new clientId and a
 * fresh WebSocket connection.
 */
const forceReconnect = async (): Promise<boolean> => {
  activeSubscription?.unsubscribe();
  activeSubscription = null;
  pubsubInstance = null;
  // Reset so we wait for a *fresh* Connected event rather than trusting the
  // stale state Amplify reports right after wake.
  latestConnectionState = null;

  await createSubscription();

  // Defer "success" until the new MQTT connection is actually established. The
  // caller refetches on success, so waiting for Connected guarantees the
  // refetch runs against a live subscription and live messages are not dropped
  // during the handshake (the cause of a missed first bid right after wake).
  return waitForConnected(CONNECTION_ESTABLISH_TIMEOUT_MS);
};

/**
 * Re-establish the IoT subscription after the device wakes from sleep or the
 * network is restored.
 *
 * Amplify reports a *stale* connection state right after wake (its keep-alive
 * timers were frozen while asleep), so when `force` is set we reconnect
 * unconditionally rather than trusting `latestConnectionState`. For lighter
 * triggers (e.g. a normal tab focus) we skip when the connection is healthy to
 * avoid needless churn.
 *
 * Returns `true` when a reconnect was performed, so the caller can refetch data
 * to catch events missed while disconnected.
 *
 * @param force reconnect even if the connection state currently looks healthy
 */
const reconnectIot = async (force = false): Promise<boolean> => {
  if (!activeTopics.length || !activeOnMessage) {
    return false;
  }

  if (
    !force &&
    (latestConnectionState === ConnectionState.Connected ||
      latestConnectionState === ConnectionState.Connecting)
  ) {
    return false;
  }

  if (reconnectInFlight) {
    return reconnectInFlight;
  }

  // Swallow reconnect failures here so callers (fire-and-forget event handlers)
  // never see an unhandled rejection. A failed attempt leaves the connection
  // torn down; the next recovery trigger will retry.
  reconnectInFlight = forceReconnect().catch((error) => {
    console.error("[Baana IOT] reconnect failed", error);
    return false;
  });
  try {
    return await reconnectInFlight;
  } finally {
    reconnectInFlight = null;
  }
};

/**
 * Invalidate the dealer auction-related queries so the UI immediately refetches
 * fresh data after a reconnect, catching any events missed during disconnection.
 */
const invalidateActiveAuctionQueries = (queryClient: QueryClient) => {
  const keysToInvalidate = [
    DealerQueryKeys.AUCTION,
    DealerQueryKeys.AUCTIONS,
    DealerQueryKeys.MY_ACTIVE_AUCTIONS,
    DealerQueryKeys.ENDED_AUCTIONS,
    DealerQueryKeys.AUCTIONS_SUMMARY
  ];

  keysToInvalidate.forEach((queryKey) => {
    queryClient.invalidateQueries({ queryKey: [queryKey] });
  });
};

export {
  invalidateActiveAuctionQueries,
  reconnectIot,
  subscribeToTopics,
  unsubscribeFromTopics,
  useIOT
};
